import { MaskedInput } from '@supply-mover-2-0/web-ui';
import React, { useCallback } from 'react';

interface CurrencyInputProps {
	/**
	 * The amount as the payment screens hold it: a plain decimal string
	 * (`"104.00"`), or `""` for no amount. Every screen already parses this
	 * shape with `parseFloat`, so the mask adapts to it rather than the
	 * other way round.
	 */
	value: string;
	/** `""` when the field is cleared, otherwise the amount fixed to cents. */
	onChange: (value: string) => void;
	disabled?: boolean;
	/** `"$"` by default; a credit row passes `"-$"`. */
	prefix?: string;
	/** Row inputs are `md`; the sidebar's single amount field is `lg`. */
	size?: 'md' | 'lg';
	className?: string;
}

/**
 * PD-15974 — the storefront's currency field, the same cents-shift mask the
 * CRM uses everywhere an amount is typed: the digits enter from the right,
 * so keying `123456` reads `$1,234.56` as it is typed, and the prefix and
 * thousands separators are drawn by the field rather than by a `$` span
 * floated over a bare number input. The whole amount is selected on focus,
 * so clicking a pre-filled field and typing replaces it.
 *
 * Two decimals are pinned here rather than read from the item price
 * precision setting the shared component consults: that setting describes
 * how PRICES are displayed, and a payment is charged in cents whatever it
 * says.
 *
 * The mask has no notion of "empty" on its own — clearing every digit is
 * `$0.00` — so the field runs in the shared component's nullable mode and
 * maps its cleared state to `""`, which is what an unchecked invoice row
 * and a not-yet-typed amount already are on every payment screen.
 *
 * Box styling comes from the storefront's base `input` rules (brand input
 * tint, border, text and radius, 44px touch height on mobile), which
 * outrank the shared component's CRM classes the same way they do for the
 * native controls beside it. The one exception is focus: the shared
 * component's `focus:` utilities outrank the base rule, so `brand-field`
 * opts into the storefront's higher-specificity brand focus ring (see
 * index.css). `className` carries only what a caller adds on top, such as
 * right alignment for a row.
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
	value,
	onChange,
	disabled = false,
	prefix = '$',
	size = 'md',
	className,
}) => {
	const parsed = value === '' ? null : parseFloat(value);
	const numeric = parsed != null && Number.isFinite(parsed) ? parsed : null;

	const handleChange = useCallback(
		(next: number): void => {
			onChange(next.toFixed(2));
		},
		[onChange],
	);
	const handleClear = useCallback((): void => {
		onChange('');
	}, [onChange]);

	return (
		<MaskedInput
			mask="currency"
			prefix={prefix}
			minDecimals={2}
			maxDecimals={2}
			nullable={true}
			value={numeric}
			onChange={handleChange}
			onClear={handleClear}
			disabled={disabled}
			size={size}
			fullWidth={true}
			className={`brand-field ${className ?? ''}`}
		/>
	);
};
